import {
  BrandBrainReadModelSchema,
  OnboardingWritesAvailabilitySchema,
  type BrandBrainReadModel,
  type OnboardingWritesAvailability,
} from "@nexus/contracts";
import { pollStatus } from "@/features/identity-hub/api/identity-hub.api";
import { authFetch } from "@/lib/api-client";

/** A worker FAILED result for the Brand Brain read. */
export class BrandBrainJobError extends Error {
  override readonly name = "BrandBrainJobError";

  constructor(
    public readonly code: string | undefined,
    message: string,
  ) {
    super(message);
  }
}

export const BRAND_BRAIN_POLL_BASE_PATH = "/brand-brain";

export async function fetchBrandBrain(): Promise<BrandBrainReadModel> {
  const idempotencyKey = crypto.randomUUID();
  const { correlationId } = await authFetch<void>(`${BRAND_BRAIN_POLL_BASE_PATH}/fetch`, {
    method: "POST",
    body: JSON.stringify({ idempotencyKey }),
  });
  if (!correlationId) {
    throw new Error("Amber response missing correlationId");
  }

  const status = await pollStatus(BRAND_BRAIN_POLL_BASE_PATH, correlationId);
  if (status.status === "FAILED") {
    throw new BrandBrainJobError(status.error, status.message ?? "Could not load Brand Brain");
  }
  return BrandBrainReadModelSchema.parse(status.result);
}

/** Whether Brand Brain edits are accepted on this deployment. The server enforces it; the UI only hides Edit. */
export async function fetchBrandBrainAvailability(): Promise<OnboardingWritesAvailability> {
  const { data } = await authFetch<unknown>(`${BRAND_BRAIN_POLL_BASE_PATH}/availability`);
  return OnboardingWritesAvailabilitySchema.parse(data);
}

/** Sends changed editable fields through the same client-context update as Identity Hub; poll on `/brand-brain/status`. */
export async function updateBrandBrain(changes: Record<string, unknown>): Promise<{ correlationId: string }> {
  const idempotencyKey = crypto.randomUUID();
  const { correlationId } = await authFetch<void>(`${BRAND_BRAIN_POLL_BASE_PATH}/update`, {
    method: "POST",
    body: JSON.stringify({ idempotencyKey, changes }),
  });
  if (!correlationId) {
    throw new Error("Amber response missing correlationId");
  }
  return { correlationId };
}
